// src/hooks/useNotificationPermission.ts
// 推送通知權限管理 Hook（重構版）

import { useState, useEffect, useCallback, useRef } from 'react';
import { getToken, onMessage } from 'firebase/messaging';
import { messaging, messagingPromise, auth } from '../config/firebase';
import { logInfo, logError, logDev } from '../utils/logger';
import { onAuthStateChanged } from 'firebase/auth';

export type PermissionState = 'default' | 'granted' | 'denied';

interface UseNotificationPermissionResult {
  permission: PermissionState;
  isSupported: boolean;
  isLoading: boolean;
  requestPermission: () => Promise<boolean>;
  fcmToken: string | null;
}

/**
 * 通知權限管理 Hook（重構版）
 *
 * 重構重點：
 * 1. 使用 useRef 防止重複初始化
 * 2. 明確指定使用主 Service Worker
 * 3. 穩定的依賴管理（使用 userId 而非 auth.currentUser）
 * 4. 錯誤處理不重試，避免無限循環
 */
export const useNotificationPermission = (): UseNotificationPermissionResult => {
  const [permission, setPermission] = useState<PermissionState>('default');
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  // 防止重複初始化的 flag
  const initializingRef = useRef(false);
  const initializedRef = useRef(false);

  // 監聽認證狀態變化（穩定的依賴）
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUserId(user?.uid || null);
    });

    return () => unsubscribe();
  }, []);

  // 檢查瀏覽器支援度（只執行一次）
  useEffect(() => {
    const checkSupport = async () => {
      if (initializedRef.current) return;
      if (initializingRef.current) return;

      initializingRef.current = true;

      try {
        // 開發環境：Service Worker 已停用，通知功能不可用
        if (process.env.NODE_ENV === 'development') {
          logDev('Notification', '開發環境：Service Worker 已停用（CRA 最佳實踐）');
          logDev('Notification', '推送通知功能僅在生產構建中可用');
          logDev('Notification', '測試方式：npm run build && npx serve -s build');
          setIsSupported(false);
          setIsLoading(false);
          initializedRef.current = true;
          initializingRef.current = false;
          return;
        }

        // 等待 messaging 初始化完成
        const messagingInstance = await messagingPromise;

        if (!messagingInstance) {
          logDev('Notification', 'Firebase Messaging 未初始化或不支援');
          setIsSupported(false);
          setIsLoading(false);
          initializedRef.current = true;
          initializingRef.current = false;
          return;
        }

        if (!('Notification' in window)) {
          logDev('Notification', '瀏覽器不支援 Notification API');
          setIsSupported(false);
          setIsLoading(false);
          initializedRef.current = true;
          initializingRef.current = false;
          return;
        }

        if (!('serviceWorker' in navigator)) {
          logDev('Notification', '瀏覽器不支援 Service Worker');
          setIsSupported(false);
          setIsLoading(false);
          initializedRef.current = true;
          initializingRef.current = false;
          return;
        }

        setIsSupported(true);
        setPermission(Notification.permission);
        initializedRef.current = true;
        setIsLoading(false);

        logInfo('Notification', '通知系統初始化完成');
      } catch (error) {
        logError('Notification', '檢查支援度失敗', error);
        setIsSupported(false);
        setIsLoading(false);
        initializedRef.current = true;
      } finally {
        initializingRef.current = false;
      }
    };

    checkSupport();
  }, []); // ✅ 只在 mount 時執行一次

  // 當用戶登入且已有權限時，自動同步 Token（但只同步一次）
  useEffect(() => {
    if (!isSupported || !userId || permission !== 'granted') return;
    if (!initializedRef.current) return; // 等待初始化完成

    const syncToken = async () => {
      try {
        const token = await retrieveToken();
        if (token) {
          const { saveFCMToken } = await import('../services/notificationTokenService');
          await saveFCMToken(token);
          logInfo('Notification', 'FCM Token 已自動同步到當前用戶');
        }
      } catch (error) {
        logError('Notification', 'FCM Token 同步失敗', error);
      }
    };

    syncToken();
  }, [userId, isSupported, permission]); // ✅ 穩定的依賴

  // 監聽前景訊息
  useEffect(() => {
    if (!messaging || !isSupported) return;

    const unsubscribe = onMessage(messaging, (payload) => {
      logInfo('Notification', '前景訊息收到');
      logDev('Notification', JSON.stringify(payload));

      // ✅ 處理 data-only 消息（後端發送純 data，無 notification 欄位）
      if (Notification.permission === 'granted' && payload.data) {
        const data = payload.data;
        const notificationId = data.notificationId || `orlo-${Date.now()}`;

        new Notification(data.title || 'Orlo', {
          body: data.body || '',
          icon: data.icon || '/logo192.png',
          tag: notificationId, // 相同 tag 會替換舊通知，防止重複
          renotify: true, // 即使使用相同 tag，仍然通知用戶
        });
      }
    });

    return () => unsubscribe();
  }, [messaging, isSupported]);

  // 取得 FCM Token（重構版：明確指定 Service Worker）
  const retrieveToken = useCallback(async (): Promise<string | null> => {
    if (!messaging || !isSupported) return null;

    // 開發環境：Service Worker 已停用，無法取得 Token
    if (process.env.NODE_ENV === 'development') {
      logDev('Notification', '開發環境無法取得 FCM Token（Service Worker 已停用）');
      return null;
    }

    try {
      const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
      if (!vapidKey) {
        logError('Notification', 'VAPID Key 未設定');
        return null;
      }

      // ✅ 關鍵修復：確保使用我們自己的 Service Worker
      // 等待主 Service Worker 準備就緒
      const registration = await navigator.serviceWorker.getRegistration('/');

      if (!registration) {
        logError('Notification', '找不到主 Service Worker，請確保 /service-worker.js 已註冊');
        return null;
      }

      // 確保 Service Worker 已經 active
      if (!registration.active) {
        logError('Notification', 'Service Worker 尚未啟用');
        return null;
      }

      logInfo('Notification', `使用 Service Worker: ${registration.scope}`);

      // ✅ 明確傳入我們的 Service Worker registration
      const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: registration
      });

      if (token) {
        logInfo('Notification', 'FCM Token 取得成功');
        setFcmToken(token);
        return token;
      } else {
        logError('Notification', 'FCM Token 取得失敗');
        return null;
      }
    } catch (error) {
      logError('Notification', 'FCM Token 取得錯誤', error);
      return null;
    }
  }, [messaging, isSupported]);

  // 請求通知權限
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      logError('Notification', '瀏覽器不支援通知');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result === 'granted') {
        logInfo('Notification', '通知權限已授予');
        const token = await retrieveToken();

        // 自動儲存 Token 到 Firestore
        if (token && userId) {
          try {
            const { saveFCMToken } = await import('../services/notificationTokenService');
            await saveFCMToken(token);
            logInfo('Notification', 'FCM Token 已自動儲存到 Firestore');
          } catch (error) {
            logError('Notification', 'FCM Token 儲存失敗', error);
            // ⚠️ 不拋出錯誤，避免影響用戶體驗
          }
        }

        return true;
      } else {
        logInfo('Notification', '通知權限被拒絕');
        return false;
      }
    } catch (error) {
      logError('Notification', '請求通知權限失敗', error);
      return false;
    }
  }, [isSupported, retrieveToken, userId]);

  return {
    permission,
    isSupported,
    isLoading,
    requestPermission,
    fcmToken,
  };
};
